<!-- src/components/GeeTestCaptcha.vue -->
<template>
  <!-- 极验验证码容器：对外事件(success/fail)与 resetSlider() 方法与旧 SliderCaptcha 完全一致 -->
  <div class="geetest-captcha">
    <!-- 极验按钮挂载点（float 模式：按钮嵌在表单里，点击后向下弹出验证面板） -->
    <div ref="geetestBox" class="geetest-box"></div>
    <!-- 验证结果提示信息 -->
    <div v-if="message" class="geetest-message" :class="messageType">
      {{ message }}
    </div>
  </div>
</template>

<script>
// 导入 API 客户端，用于请求后端配置与二次校验接口
import apiClient from '@/api/index';

export default {
  // 组件名称
  name: 'GeeTestCaptcha',

  // 组件内部数据
  data() {
    return {
      captchaId: '',      // 极验验证 ID（从后端获取，换凭证无需重新打包前端）
      captchaObj: null,   // 极验验证对象（initGeetest4 回调返回）
      verified: false,    // 是否已通过验证（后端二次校验成功）
      message: '',        // 提示信息文本
      messageType: ''     // 提示信息类型：success / error
    };
  },

  // 组件挂载后初始化
  mounted() {
    // 获取极验配置并初始化验证组件
    this.initCaptcha();
  },

  // 组件销毁前清理极验实例，防止 DOM 残留
  beforeDestroy() {
    if (this.captchaObj) {
      try {
        this.captchaObj.destroy();
      } catch (e) {
        // 忽略销毁时的异常
      }
      this.captchaObj = null;
    }
  },

  // 组件方法
  methods: {
    // 从后端获取 captcha_id 并初始化极验
    async initCaptcha() {
      try {
        const response = await apiClient.get('/captcha/geetest/config');
        if (response.data.code !== 200) {
          this.showError(response.data.msg || '获取验证配置失败');
          return;
        }
        this.captchaId = response.data.data.captcha_id;
        this.initGeetest();
      } catch (error) {
        console.error('获取极验配置失败:', error);
        this.showError('验证组件加载失败，请刷新重试');
      }
    },

    // 初始化极验 4.0 验证组件（依赖 index.html 引入的 gt4.js）
    initGeetest() {
      // gt4.js 未加载成功时的兜底提示
      if (!window.initGeetest4) {
        this.showError('验证脚本加载失败，请检查网络后刷新');
        return;
      }

      // 初始化极验验证对象，float 模式按钮嵌入表单、面板向下弹出
      window.initGeetest4(
        {
          captchaId: this.captchaId,   // 验证 ID
          product: 'float',            // 产品形态：浮动式（嵌入按钮 + 弹出面板）
          language: 'zh-cn'            // 界面语言
        },
        (captchaObj) => {
          // 保存验证对象供后续调用
          this.captchaObj = captchaObj;
          // 先绑定事件再渲染，避免渲染完成前验证已触发导致回调丢失
          // 验证成功回调（用户完成滑动/智能验证后触发）
          captchaObj.onSuccess(this.onVerifySuccess);
          // 验证出错回调（网络异常等）
          captchaObj.onError(() => {
            this.showError('验证出错，请重试');
            this.$emit('fail');
          });
          // 将极验按钮渲染到容器内（热更新/DOM 竞态可能抛错，兜底避免冒泡成全局异常）
          try {
            captchaObj.appendTo(this.$refs.geetestBox);
          } catch (e) {
            console.error('极验按钮渲染失败:', e);
            this.showError('验证组件渲染失败，请刷新重试');
          }
        }
      );
    },

    // 极验前端验证成功：拿四元组到后端做二次校验换取 slider_token
    async onVerifySuccess() {
      // getValidate 返回 { lot_number, captcha_output, pass_token, gen_time }
      const result = this.captchaObj ? this.captchaObj.getValidate() : null;
      if (!result) {
        // 拿不到校验结果时重置重来
        this.resetSlider();
        return;
      }

      try {
        // 提交四元组到后端，由服务端向极验二次校验
        const response = await apiClient.post('/captcha/geetest/verify', {
          lot_number: result.lot_number,         // 验证流水号
          captcha_output: result.captcha_output, // 验证输出凭证
          pass_token: result.pass_token,         // 一次性通行令牌
          gen_time: result.gen_time              // 验证通过时间戳
        });

        if (response.data.code === 200) {
          // 二次校验通过，保存状态并通知父组件（与旧滑块相同的 slider_token）
          this.verified = true;
          this.message = '验证成功';
          this.messageType = 'success';
          this.$emit('success', response.data.data.slider_token);
        } else {
          // 后端校验失败（凭证过期/被重放等），重置组件要求重新验证
          this.resetSlider();
          this.showError(response.data.msg || '验证失败，请重试');
          this.$emit('fail');
        }
      } catch (error) {
        // 网络异常处理
        this.resetSlider();
        this.showError('网络错误，请重试');
        this.$emit('fail');
        console.error('极验二次校验失败:', error);
      }
    },

    // 重置验证组件（方法名与旧 SliderCaptcha 保持一致，父组件无需改动）
    // 登录失败/令牌过期时由父组件调用，清空状态并恢复可验证
    resetSlider() {
      this.verified = false;
      this.message = '';
      this.messageType = '';
      if (this.captchaObj) {
        // 极验内置重置：恢复按钮初始状态，可重新验证
        this.captchaObj.reset();
      }
    },

    // 显示错误提示
    showError(msg) {
      this.message = msg;
      this.messageType = 'error';
    }
  }
};
</script>

<style scoped>
/* 极验验证码外层容器 */
.geetest-captcha {
  user-select: none;
  -webkit-user-select: none;
}

/* 极验按钮挂载容器：宽度与旧滑块轨道(300px)保持一致 */
.geetest-box {
  width: 300px;
  max-width: 100%;
}

/* 极验 float 按钮宽度撑满容器（穿透 scoped 作用到极验生成的 DOM） */
.geetest-box :deep(.geetest_box) {
  width: 100%;
}

/* 提示信息 */
.geetest-message {
  margin-top: 6px;
  font-size: 12px;
  text-align: center;
}

.geetest-message.success {
  color: #52c41a;
}

.geetest-message.error {
  color: #ff4d4f;
}
</style>
